import type { PromotionType } from '@/types';
import {
  fetchAreAllEligibleProductsInactiveOrSoldOutDocument,
  fetchCancelAllMarkUpDocument,
  fetchCancelGiftDocument,
  fetchCancelMarkUpDocument,
  fetchDiscountActivityDocument,
  fetchFreeGiftByOrderDocument,
  fetchFullDiscountPresentDocument,
  fetchMarkUpByOrderDocument,
  fetchOrderPaymentRewardCouponsDocument,
  fetchPackageDiscountDocument,
  fetchProductActivitiesFindAllDocument,
  fetchProductActivitiesFindOneDocument,
  fetchPurchasePremiumDocument,
  fetchReceivePaymentRewardCouponDocument,
  fetchSelectGiftDocument,
  fetchSelectMarkUpDocument,
  fetchShoppingCreditsClaimActivityDocument,
  fetchShoppingCreditsDeductionActivityDocument,
} from '../graphql/docs';
import { fetcher } from '../graphql/fetcher/index';

/**
 * 商品对应的所有活动信息数据
 * @param productId - 商品id
 */
export const fetchProductActivitiesFindAll = async (productId: string) => {
  const res = await fetcher.query(fetchProductActivitiesFindAllDocument, {
    variables: {
      productId,
    },
  });
  return res.data.value?.productActivitiesFindAll;
};

/**
 * 商品对应的单项活动信息数据
 * @param activityId - 活动id
 */
export const fetchProductActivitiesFindOne = async (activityId: string) => {
  const res = await fetcher.query(fetchProductActivitiesFindOneDocument, {
    variables: {
      id: activityId,
    },
  });
  return res.data.value?.productActivitiesFindOne;
};

/**
 * 添加赠品
 * @param orderId
 * @param skuId
 * @param giftId
 * @param promInstanceId
 */
export const fetchSelectGift = async (
  orderId: string,
  skuId: string,
  giftId: string,
  promInstanceId: string,
  ladderLevel: number
) => {
  const res = await fetcher.mutation(fetchSelectGiftDocument, {
    orderId,
    skuId,
    giftId,
    promInstanceId,
    ladderLevel,
  });
  return res.data?.selectGift;
};

/**
 * 取消赠品
 * @param orderId
 */
export const fetchCancelGift = async (orderId: string, giftId: string, promInstanceId: string) => {
  const res = await fetcher.mutation(fetchCancelGiftDocument, {
    orderId,
    giftId,
    promInstanceId,
  });
  return res.data?.cancelGift;
};

/**
 * 选择优惠换购商品
 * @param orderId
 * @param skuId
 * @param productId
 * @param promInstanceId
 */
export const fetchSelectMarkUp = async (
  orderId: string,
  skuId: string,
  productId: string,
  promInstanceId: string
) => {
  const res = await fetcher.mutation(fetchSelectMarkUpDocument, {
    orderId,
    skuId,
    productId,
    promInstanceId,
  });
  return res.data?.selectMarkUp;
};

/**
 * 取消选择优惠换购商品
 * @param orderId
 * @param productId
 * @param promInstanceId
 */
export const fetchCancelMarkUp = async (
  orderId: string,
  productId: string,
  promInstanceId: string
) => {
  const res = await fetcher.mutation(fetchCancelMarkUpDocument, {
    orderId,
    productId,
    promInstanceId,
  });
  return res.data?.cancelMarkUp;
};

/**
 * 根据订单id查询赠品
 * @param orderId - 订单id
 * @returns
 */
export const fetchFreeGiftByOrderId = async (orderId: string) => {
  const res = await fetcher.query(fetchFreeGiftByOrderDocument, {
    variables: {
      orderId,
    },
  });
  return res.data.value?.freeGiftByOrder;
};

/**
 * 根据订单id查询换购商品
 * @param orderId - 订单id
 * @returns
 */
export const fetchMarkUpByOrder = async (orderId: string) => {
  const res = await fetcher.query(fetchMarkUpByOrderDocument, {
    variables: {
      orderId,
    },
  });
  return res.data.value?.markUpByOrder;
};

/**
 * 取消加价购全部商品
 * @param orderId - 订单id
 */
export const fetchCancelAllMarkUp = async (orderId: string) => {
  const res = await fetcher.mutation(fetchCancelAllMarkUpDocument, {
    orderId,
  });
  return res.data?.cancelAllMarkUp;
};

/**
 * 获取满减活动详情
 * @param id - 活动id
 */
export const fetchFullDiscountPresent = async (id: string) => {
  const res = await fetcher.query(fetchFullDiscountPresentDocument, {
    variables: { id },
  });
  return res.data.value?.fullDiscountPresent;
};

/**
 * 获取x件y折活动详情
 * @param id - 活动id
 */
export const fetchDiscountActivity = async (discountActivityId: string) => {
  const res = await fetcher.query(fetchDiscountActivityDocument, {
    variables: { discountActivityId },
  });
  return res.data.value?.discountActivity;
};

/**
 * 获取加价购活动详情
 * @param id - 活动id
 */
export const fetchPurchasePremium = async (purchasePremiumId: string) => {
  const res = await fetcher.query(fetchPurchasePremiumDocument, {
    variables: { purchasePremiumId },
  });
  return res.data.value?.purchasePremium;
};

/**
 * 打包一口价
 * @param id
 * @returns
 */
export const fetchPackageDiscount = async (id: string) => {
  const res = await fetcher.query(fetchPackageDiscountDocument, {
    variables: { id },
  });
  return res.data.value?.packageDiscount;
};

/**
 * 根据订单查询支付有礼优惠券
 * @param orderId
 * @returns
 */
export const fetchOrderPaymentRewardCoupons = async (orderId: string) => {
  const res = await fetcher.query(fetchOrderPaymentRewardCouponsDocument, {
    variables: { orderId },
  });
  return res.data.value?.orderPaymentRewardCoupons;
};

// 支付有礼 - 领取优惠券
export const fetchReceivePaymentRewardCoupon = async (orderPaymentRewardCouponId: string) => {
  const res = await fetcher.mutation(fetchReceivePaymentRewardCouponDocument, {
    orderPaymentRewardCouponId,
  });
  return res.data?.receivePaymentRewardCoupon;
};

/**
 * 查询当前活动商品是否异常
 * @param orderPaymentRewardCouponId
 * @returns
 */
export const fetchAreAllEligibleProductsInactiveOrSoldOut = async (
  promotionId: string,
  promotionType: PromotionType
) => {
  const res = await fetcher.query(fetchAreAllEligibleProductsInactiveOrSoldOutDocument, {
    variables: {
      promotionId,
      promotionType,
    },
  });
  return res.data.value?.areAllEligibleProductsInactiveOrSoldOut;
};

/**
 * 获取购物金活动详情
 * @param id
 * @returns
 */
export const fetchShoppingCreditsClaimActivity = async (shoppingCreditsClaimActivityId: string) => {
  const res = await fetcher.query(fetchShoppingCreditsClaimActivityDocument, {
    variables: { shoppingCreditsClaimActivityId },
  });
  return res.data.value?.shoppingCreditsClaimActivity;
};

/**
 * 获取购物金抵扣活动详情
 * @param id
 * @returns
 */
export const fetchShoppingCreditsDeductionActivity = async (
  shoppingCreditsDeductionActivityId: string
) => {
  const res = await fetcher.query(fetchShoppingCreditsDeductionActivityDocument, {
    variables: { shoppingCreditsDeductionActivityId },
  });
  return res.data.value?.shoppingCreditsDeductionActivity;
};
